import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { ToastTypes } from '@type/toast';
import { API_RESPONSE_STATUS, CompanyProductType } from '@constants/common';
import { showGenericErrorToast } from '@utils/error';
import displayToast from '@components/toast/customToast';

import {
  useAddProductAssetMutation,
  useAddProductMutation,
  useDeleteProductAssetMutation,
  useDeleteProductMutation,
  useUpdateProductMutation
} from '../api';
import { companyProductFieldType, productRequestType } from '../types';
import { SectionMapper } from '../constants';

const useCreateProduct = ({
  updateEditMode
}: {
  updateEditMode: ((section?: SectionMapper | undefined) => void) | undefined;
}) => {
  const { companyId } = useParams();

  const [createProduct, { isLoading: addProductLoading }] =
    useAddProductMutation();
  const [updateProduct, { isLoading: updateProductLoading }] =
    useUpdateProductMutation();
  const [addProductAsset, { isLoading: addProductAssetLoading }] =
    useAddProductAssetMutation();
  const [deleteProductAsset, { isLoading: deleteProductAssetLoading }] =
    useDeleteProductAssetMutation();
  const [deleteProduct, { isLoading: productDeleteLoading }] =
    useDeleteProductMutation();

  const [deletedProductAssetIds, setDeletedProductAssetIds] = useState<
    string[]
  >([]);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState<
    'discard' | 'save' | undefined
  >();

  const onCreateUpdateProduct = (
    productFormFieldValues: companyProductFieldType
  ) => {
    productFormFieldValues.productId !== ''
      ? onUpdateProduct({
          productId: productFormFieldValues.productId,
          ...getFormattedProductData(productFormFieldValues)
        })
      : onCreateProduct(getFormattedProductData(productFormFieldValues));
  };

  const getFormattedProductData = (productData: companyProductFieldType) => {
    return {
      name: productData.productName,
      status: productData.productStatus,
      description: productData.productDescription,
      ios_app_url: productData.iosAppLink,
      android_app_url: productData.androidAppLink,
      web_app_url: productData.webAppLink,
      assets: [
        ...(productData.appScreenshots
          ?.filter(assets => assets.path !== '')
          ?.map(val => ({
            id: val.id ?? '',
            path: val.path ?? '',
            asset_type: val.file?.type ?? '',
            source: CompanyProductType.APP,
            order: val.order
          })) || []),
        ...(productData.webScreenshots
          ?.filter(assets => assets.path !== '')
          ?.map(val => ({
            id: val.id ?? '',
            path: val.path ?? '',
            asset_type: val.file?.type ?? '',
            source: CompanyProductType.WEB,
            order: val.order
          })) || [])
      ]
    };
  };

  const onCreateProduct = async (productData: productRequestType) => {
    try {
      const response = await createProduct({
        company_id: companyId ?? '',
        payload: {
          ...productData
        }
      }).unwrap();

      if (response.status === API_RESPONSE_STATUS.OK) {
        displayToast(ToastTypes.SUCCESS, 'Product Added Successfully');
        updateEditMode?.();
      } else {
        displayToast(
          ToastTypes.ERROR,
          response.error?.details?.[0] ?? 'Failed to add Product '
        );
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
    setConfirmModalOpen(undefined);
  };

  const onUpdateProduct = async (
    productData: { productId: string } & productRequestType
  ) => {
    try {
      if (deletedProductAssetIds.length) {
        await onDeleteProductAssets(productData);
      }

      const addAssetsData = productData.assets.filter(item => item.id === '');
      if (addAssetsData.length)
        await onAddProductAssets(productData.productId, addAssetsData);

      const ProductResponse = await updateProduct({
        product_id: productData.productId,
        company_id: companyId ?? '',
        payload: {
          name: productData.name,
          status: productData.status,
          description: productData.description,
          ios_app_url: productData.ios_app_url,
          android_app_url: productData.android_app_url,
          web_app_url: productData.web_app_url
        }
      }).unwrap();

      if (ProductResponse.status === API_RESPONSE_STATUS.OK) {
        displayToast(ToastTypes.SUCCESS, 'Product updated Successfully');
        setConfirmModalOpen(undefined);
        updateEditMode?.();
      } else {
        displayToast(
          ToastTypes.ERROR,
          ProductResponse.error?.details?.[0] ?? 'Failed to Update Product '
        );
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
    setConfirmModalOpen(undefined);
  };

  const onDeleteProductAssets = async (
    productData: { productId: string } & productRequestType
  ) => {
    try {
      const deleteProductAssetRes = await Promise.allSettled(
        deletedProductAssetIds
          .filter(val => !!val)
          .map(val =>
            deleteProductAsset({
              asset_id: val,
              company_id: companyId ?? '',
              product_id: productData.productId
            }).unwrap()
          )
      );
      if (deleteProductAssetRes.every(item => item.status === 'fulfilled')) {
        setDeletedProductAssetIds([]);
        setConfirmModalOpen(undefined);
        return true;
      }
      displayToast(ToastTypes.ERROR, 'Failed to delete screenshot');
      return false;
    } catch (err) {
      showGenericErrorToast(err);
      return false;
    }
  };

  const onAddProductAssets = async (
    productId: string,
    assetData: {
      id: string;
      path: string;
      asset_type: string;
      source: string;
      order: number;
    }[]
  ) => {
    try {
      const addProductAssetRes = await Promise.allSettled(
        assetData.map(val =>
          addProductAsset({
            company_id: companyId ?? '',
            product_id: productId,
            payload: {
              asset_type: val.asset_type,
              path: val.path,
              source: val.source,
              order: val.order
            }
          }).unwrap()
        )
      );
      if (addProductAssetRes.every(item => item.status === 'fulfilled')) {
        setConfirmModalOpen(undefined);
        return true;
      }
      displayToast(ToastTypes.ERROR, 'Failed to add product screenshot');
      return false;
    } catch (err) {
      showGenericErrorToast(err);
      return false;
    }
  };

  const onDeleteProduct = async (productId: string) => {
    try {
      const deleteProductResponse = await deleteProduct({
        company_id: companyId ?? '',
        product_id: productId
      }).unwrap();

      if (deleteProductResponse.status === API_RESPONSE_STATUS.OK) {
        displayToast(ToastTypes.SUCCESS, 'Product Deleted Successfully');
        updateEditMode?.();
      } else {
        displayToast(
          ToastTypes.ERROR,
          deleteProductResponse.error?.details?.[0] ??
            'Failed to delete Product'
        );
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  return {
    onCreateUpdateProduct,
    setDeletedProductAssetIds,
    deletedProductAssetIds,
    isConfirmModalOpen,
    setConfirmModalOpen,
    onDeleteProduct,
    productDeleteLoading,
    isLoading:
      addProductLoading ||
      deleteProductAssetLoading ||
      updateProductLoading ||
      addProductAssetLoading ||
      productDeleteLoading
  };
};

export default useCreateProduct;
